import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SYSTEM_COLORS } from "../../../../../components/system.colors";

const getCurrentMonthIndex = (monthKeys) => {
  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return monthKeys.indexOf(currentKey);
};

const TOOLTIP_OFFSET = 8;
const TOOLTIP_FLIP_THRESHOLD = 160;

const BreakdownTooltip = ({ tooltip }) => {
  if (!tooltip) return null;

  const { title, items, left, top, placement } = tooltip;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left,
        top,
        transform: placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
        zIndex: 1000,
        pointerEvents: 'none',
        minWidth: 160,
        padding: '8px 10px',
        borderRadius: 6,
        background: SYSTEM_COLORS.BLACK.JET,
        color: SYSTEM_COLORS.WHITE.SEASHELL,
        fontSize: 12,
        lineHeight: 1.4,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
        whiteSpace: 'nowrap',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6, color: SYSTEM_COLORS.GRAY.GRAY_187 }}>
        {title}
      </div>
      {items.map((item) => (
        <div
          key={item.label}
          style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}
        >
          <span>{item.label}</span>
          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{item.formatted}</span>
        </div>
      ))}
    </div>,
    document.body
  );
};

export const UnifiedConsolidadoTable = ({ actions, ctx, uiState }) => {
  const { onEditField, onOtbLinkClick } = actions || {};
  const { monthKeys, months, summaryPeriods, groups } = ctx || {};
  const { 
    showSemestres = true, 
    showTotal = true,
    isEditing = false
  } = uiState || {};

  const wrapRef = useRef(null);
  const monthHeaderRefs = useRef({});
  const hasScrolledRef = useRef(false);
  const [tooltip, setTooltip] = useState(null);

  const currentMonthIndex = monthKeys ? getCurrentMonthIndex(monthKeys) : -1;

  useLayoutEffect(() => {
    if (hasScrolledRef.current || !groups || currentMonthIndex === -1) return;

    const wrap = wrapRef.current;
    const targetHeader = monthHeaderRefs.current[Math.max(currentMonthIndex - 1, 0)];
    if (!wrap || !targetHeader) return;

    const stickyWidth = [...wrap.querySelectorAll('.otb-th-grupo-col, .otb-th-metrica-col')]
      .filter((el) => getComputedStyle(el).position === 'sticky')
      .reduce((sum, el) => sum + el.offsetWidth, 0);

    wrap.scrollLeft = targetHeader.offsetLeft - stickyWidth;
    hasScrolledRef.current = true;
  }, [groups, currentMonthIndex]);  

  const showTooltip = (event, title, items) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const placement = rect.top < TOOLTIP_FLIP_THRESHOLD ? 'bottom' : 'top';
    setTooltip({
      title,
      items,
      placement,
      left: rect.left + rect.width / 2,
      top: placement === 'top' ? rect.top - TOOLTIP_OFFSET : rect.bottom + TOOLTIP_OFFSET,
    });
  };

  const hideTooltip = () => setTooltip(null);

  if (!groups) {
    return <div className="otb-no-data">Dados não disponíveis.</div>;
  }

  const orderedColumns = [];

  months.forEach((month, index) => {
    orderedColumns.push({ type: "month", index, month });
    (summaryPeriods ?? []).forEach((period, periodIndex) => {
      if (period.afterIndex !== index) return;
      if (period.type === "semester" && !showSemestres) return;
      if (period.type === "total" && !showTotal) return;
      orderedColumns.push({ type: "sem", key: period.key, label: period.label, periodIndex, isTotal: period.type === "total" });
    });
  });

  return (
    <section ref={wrapRef} className="otb-table-wrap" onScroll={tooltip ? hideTooltip : undefined}>
      <table className="otb-table">
        <colgroup>
          <col className="otb-col-grupo" />
          <col className="otb-col-metrica" />
          {orderedColumns.map((col) => (
            col.type === "month"
              ? <col key={`m-${col.index}`} className="otb-col-month" style={isEditing ? { width: 110, minWidth: 110 } : undefined} />
              : <col key={col.key} className="otb-col-sem" />
          ))}
        </colgroup>

        <thead>
          <tr>
            <th className="otb-th-grupo-col">GRUPO</th>
            <th className="otb-th-metrica-col">MÉTRICA</th>

            {orderedColumns.map((col) => {
              if (col.type === "month") {
                const { index, month } = col;
                const isCurrent = index === currentMonthIndex;
                const isPast = currentMonthIndex !== -1 && index < currentMonthIndex;
                return (
                  <th
                    key={`m-${index}`}
                    ref={(el) => { monthHeaderRefs.current[index] = el; }}
                    className={`otb-th-month ${isCurrent ? "otb-th-month--current" : isPast ? "otb-th-month--past" : ""}`}
                  >
                    {isCurrent && <div className="otb-th-month-badge">ATUAL</div>}
                    {month}
                  </th>
                );
              }
              return (
                <th key={col.key} className="otb-th-sem">
                  {col.label}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {groups.flatMap((group, groupIndex) => {
            const isLastGroup = groupIndex === groups.length - 1;
            const groupBorderClass = isLastGroup ? "otb-row--border-none" : "otb-row--border-strong";

            return group.rows.map((row, i) => {
              const isLastInGroup = i === group.rows.length - 1;
              const isLastRowOfTable = isLastGroup && isLastInGroup;
              const rowBorderClass = isLastRowOfTable
                ? "otb-row--border-none"
                : isLastInGroup ? "otb-row--border-strong" : "otb-row--border-default";

              return (
              <tr key={row.key} className={rowBorderClass}>
                {i === 0 && (
                  <td rowSpan={group.rows.length} className={`otb-td-grupo ${groupBorderClass}`}>{group.grupo}</td>
                )}

                <td className={`otb-td-metrica ${ row.bold ? "otb-td-metrica--bold" : "" } ${rowBorderClass}`}>
                  {row.forecastIcon && 
                    <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.ORANGE.BURNT_ORANGE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                      <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
                    </svg>
                  }
                  {" "}{row.label}
                </td>

                {orderedColumns.map((col) => {
                  if (col.type === "month") {
                    const colIndex = col.index;
                    const value = row.values ? row.values[colIndex] : null;
                    const raw = row.rawValues ? row.rawValues[colIndex] : value;
                    const isCurrent = colIndex === currentMonthIndex;
                    const isPast = currentMonthIndex !== -1 && colIndex < currentMonthIndex;
                    const isFuture = currentMonthIndex !== -1 && colIndex > currentMonthIndex;

                    const dynamicColor = row.colorFn ? row.colorFn(raw) : null;
                    const dynamicBg = isFuture && row.bgFn ? row.bgFn(raw, colIndex) : null;

                    let stateClass = "";
                    if (isCurrent) stateClass = "otb-td-month--current";
                    else if (!dynamicBg && isPast) stateClass = "otb-td-month--past";

                    const isEditableMonth = isCurrent || isFuture;
                    const isEditableCell = isEditing && row.editableField != null && isEditableMonth;

                    const displayValue = raw != null ? Math.round(raw * 100) / 100 : "";

                    const breakdown = !isEditableCell ? row.breakdowns?.[colIndex] : null;

                    return (
                      <td
                        key={`m-${colIndex}`}
                        className={
                          `otb-td-month ${row.bold ? "otb-td-month--bold" : ""} 
                          ${!dynamicColor && row.placeholder ? "otb-td-month--placeholder" : ""} 
                          ${stateClass} ${rowBorderClass}`}
                        style={{
                          color: dynamicColor ?? undefined,
                          backgroundColor: isEditing ? SYSTEM_COLORS.GRAY.ZINC_100 : dynamicBg ?? undefined,
                        }}
                        onMouseEnter={breakdown ? (e) => showTooltip(e, `${row.label} · ${col.month}`, breakdown) : undefined}
                        onMouseLeave={breakdown ? hideTooltip : undefined}
                      >
                        {isEditableCell ? (
                        <input
                          key={`${row.key}-${colIndex}-${raw}`}
                          type="number"
                          defaultValue={displayValue}
                          className="otb-td-month-input"
                          style={{
                            width: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            padding: '4px 6px',
                            border: `1px solid ${SYSTEM_COLORS.ORANGE.BURNT_ORANGE_40}`,
                            borderRadius: 6,
                            background: SYSTEM_COLORS.WHITE.SEASHELL,
                            color: SYSTEM_COLORS.ORANGE.CINNABAR,
                            fontSize: 13,
                            fontWeight: 700,
                            fontFamily: 'inherit',
                            textAlign: 'right',
                            outline: 'none',
                            MozAppearance: 'textfield',
                          }}
                          onBlur={(e) => {
                            const newValue = parseFloat(e.target.value);
                            const oldValueRounded = raw != null ? Math.round(raw * 100) / 100 : 0;
                            if (isNaN(newValue) || newValue === oldValueRounded) return;
                            onEditField?.(row.editableField, colIndex, e.target.value);
                          }}
                        />
                      ) : row.linkOnCurrentMonth && isCurrent && onOtbLinkClick && raw > 0 ? (
                        <button
                          type="button"
                          onClick={() => onOtbLinkClick({
                            monthKey: monthKeys[colIndex],
                            month: col.month,
                            units: raw,
                          })}
                          style={{
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            color: "inherit",
                            font: "inherit",
                            fontWeight: "inherit",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          {value}
                        </button>
                      ) : (
                        value ?? "—"
                      )}
                      </td>
                    );
                  }

                
                  const semValue = (row.semValues ?? row.semesterValuesSummary)?.[col.periodIndex] ?? null;
                  const rawSem = row.semRawValues ? row.semRawValues[col.periodIndex] : semValue;
                  const isTotalCol = col.isTotal;

                  const dynamicSemColor = row.colorFn && rawSem != null ? row.colorFn(rawSem) : null;
                  const dynamicSemBg = row.semBgFn && rawSem != null ? row.semBgFn(rawSem) : null;
                  const isEmpty = !dynamicSemColor && semValue == null;

                  return (
                    <td
                      key={col.key}
                      className={
                        `otb-td-sem ${(row.bold || isTotalCol) ? "otb-td-sem--bold" : ""} 
                        ${isEmpty ? "otb-td-sem--empty" : ""} 
                        ${rowBorderClass}`}
                      style={{
                        color: dynamicSemColor ?? undefined,
                        backgroundColor: isEditing ? SYSTEM_COLORS.GRAY.ZINC_100 : dynamicSemBg ?? undefined,
                      }}>
                      {semValue ?? "—"}
                    </td>
                  );
                })}
              </tr>
              );
            });
          })}
        </tbody>
      </table>
      <BreakdownTooltip tooltip={tooltip} />
    </section>
  );
};